import React, { useState, useEffect, useRef, CSSProperties } from 'react'
import defaultClasses from './heroBanner.module.scss'
import { mergeClasses } from '@corratech/pylot-rich-content/src/PageBuilder/classify'
import resolveLinkProps from '@corratech/pylot-rich-content/src/PageBuilder/resolveLinkProps'
import Link from 'next/link'
import { getFastlyUrl } from '@corratech/pylot-image'
import cn from 'classnames'
import s from './HeroSlides.module.scss'

type ButtonType = 'primary' | 'secondary' | 'link' | 'catalog'
type AppearanceType = 'collage-left' | 'collage-centered' | 'collage-right'
type ShowOverlayType = 'always' | 'hover' | 'never'
enum ShowButtonType {
    'true' = 'true',
    'false' = 'false'
}

interface HeroBannerSlideProps {
    classes?: {
        root?: string
        link?: string
        wrapper?: string
        overlay?: string
        button?: string
        buttonHover?: string
        posterOverlay?: string
        posterOverlayHover?: string
        collageLeftOverlay?: string
        collageLeftOverlayHover?: string
        collageCenteredOverlay?: string
        collageCenteredOverlayHover?: string
        collageRightOverlay?: string
        collageRightOverlayHover?: string
        viewportElement?: string
    }
    appearance?: AppearanceType
    minHeight?: string
    backgroundColor?: string
    desktop_image?: string
    mobile_image?: string
    backgroundSize?: string
    backgroundPosition?: string
    backgroundRepeat?: boolean
    link?: string
    showButton?: ShowButtonType
    slideHeight?: string
    buttonText?: string
    heading?: string
    subHeading?: string
    buttonType?: ButtonType
    showOverlay?: ShowOverlayType
    overlayColor?: string
    textColor?: string
    backgroundType?: string
    videoSrc?: string
    videoMobileSource?: string
    videoLoop?: boolean
    imageQuality?: number
    showEdgesOfAdjacentSlide?: boolean
    kicker?: string
    heroBannerTheme?: string
}
const fullScreenSliderHeight = '600px'
const compactSliderHeight = '520px'

const handleDragStart = (event: React.DragEvent) => event.preventDefault()

// Poster image for a Vimeo video — matches player.vimeo.com/video/<id> and
// plain vimeo.com/<id> style URLs.
export const getVimeoPoster = (src?: string | null): string | null => {
    const vimeoId = src?.match(/vimeo\.com\/(?:.*\/)?(\d+)/)?.[1]
    return vimeoId ? `https://vumbnail.com/${vimeoId}.jpg` : null
}

const HeroBannerSlide: React.FC<HeroBannerSlideProps> = (props) => {
    const backgroundElement = useRef<HTMLDivElement | null>(null)
    const viewportElement = useRef<HTMLDivElement | null>(null) // Added reference for viewportElement
    const classes = mergeClasses(defaultClasses, props.classes || {})

    const {
        appearance = 'collage-left',
        desktop_image,
        mobile_image,
        backgroundSize,
        backgroundPosition,
        showButton,
        buttonType,
        buttonText,
        heading,
        subHeading,
        link,
        backgroundType,
        videoSrc,
        videoMobileSource,
        videoLoop,
        imageQuality,
        showEdgesOfAdjacentSlide,
        kicker,
        heroBannerTheme
    } = props

    const slideHeight = showEdgesOfAdjacentSlide
        ? compactSliderHeight
        : fullScreenSliderHeight

    const isSlideClickable = buttonType === 'link' && !!link

    const SlideWrapper = isSlideClickable ? 'a' : 'div'

    // Initiate jarallax for background video
    useEffect(() => {
        if (backgroundType !== 'video') return

        let cancelled = false
        const parallaxElement = backgroundElement.current
        let jarallax: any

        const applyParallax = async () => {
            if (!parallaxElement) return

            try {
                // @ts-ignore
                const imported = await import('jarallax')
                jarallax =
                    imported.jarallax ||
                    imported.default?.jarallax ||
                    imported.default
                const jarallaxVideo =
                    imported.jarallaxVideo || imported.default?.jarallaxVideo

                if (cancelled || typeof jarallax !== 'function') return

                jarallaxVideo?.(jarallax)

                const isMobileViewport = window.innerWidth <= 767
                const src = isMobileViewport
                    ? videoMobileSource || videoSrc
                    : videoSrc
                const posterImg =
                    (isMobileViewport && mobile_image) || desktop_image

                jarallax(parallaxElement, {
                    speed: 1,
                    imgSrc:
                        (posterImg &&
                            getFastlyUrl({
                                src: posterImg,
                                quality: imageQuality,
                                width: isMobileViewport ? 800 : 1800
                            })) ||
                        getVimeoPoster(src) ||
                        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
                    elementInViewport:
                        viewportElement.current || parallaxElement,
                    videoSrc: src,
                    videoLoop: videoLoop !== false,
                    videoLazyLoading: false,
                    videoPlayOnlyVisible: false
                })

                const instance = (parallaxElement as any).jarallax
                const showVideo = () => {
                    if (instance?.$video) {
                        instance.$video.style.visibility = 'visible'
                    }
                }
                showVideo()
                instance?.video?.on?.('started', showVideo)

                requestAnimationFrame(() => {
                    if (!cancelled) jarallax(parallaxElement, 'onScroll')
                })
            } catch (error) {
                console.error('Error loading jarallax:', error)
            }
        }

        applyParallax()

        return () => {
            cancelled = true
            if (typeof jarallax === 'function' && parallaxElement) {
                jarallax(parallaxElement, 'destroy')
            }
        }
    }, [backgroundType, videoSrc, videoMobileSource, videoLoop])

    const wrapperStyles = {
        objectFit: backgroundSize,
        objectPosition: backgroundPosition,
        position: 'absolute',
        height: slideHeight,
        width: '100%'
    }

    const appearanceOverlayClasses: Record<AppearanceType, string> = {
        'collage-left': classes.collageLeftOverlay || '',
        'collage-centered': classes.collageCenteredOverlay || '',
        'collage-right': classes.collageRightOverlay || ''
    }

    const typeToPriorityMapping: Record<ButtonType, string> = {
        primary: 'high',
        catalog: 'high',
        secondary: 'normal',
        link: 'low'
    }

    const buttonClasses: Record<ButtonType, string> = {
        primary: 'btn-primary',
        secondary: 'btn-secondary',
        catalog: 'btn-catalog',
        link: 'btn-link'
    }

    const sharedButtonClass = cn(
        defaultClasses['hero-banner-button'],
        defaultClasses[buttonClasses[buttonType || 'primary']],
        buttonClasses[buttonType || 'primary']
    )

    let buttonLink: React.ReactNode
    if (typeof link === 'string') {
        const linkProps = resolveLinkProps(link, 'default')
        buttonLink = linkProps.to ? (
            <Link {...linkProps} passHref>
                <a
                    className={sharedButtonClass}
                    onDragStart={handleDragStart}
                    data-priority={
                        typeToPriorityMapping[buttonType || 'primary']
                    }
                >
                    {buttonText}
                </a>
            </Link>
        ) : (
            <a
                {...linkProps}
                onDragStart={handleDragStart}
                className={cn(
                    defaultClasses['hero-banner-link'],
                    sharedButtonClass
                )}
                data-priority={typeToPriorityMapping[buttonType || 'primary']}
            >
                {buttonText}
            </a>
        )
    }

    const shouldRenderButton =
        showButton !== ShowButtonType.false && buttonType !== 'link'
    const BannerButton = shouldRenderButton ? (
        <div className={classes.button}>{buttonLink}</div>
    ) : null

    const HeadingTag = showEdgesOfAdjacentSlide ? 'h2' : 'h1'

    return (
        <SlideWrapper
            {...(isSlideClickable
                ? {
                      href: link,
                      className: cn(classes?.root, defaultClasses.slideLink),
                      'aria-label': heading || buttonText
                  }
                : {
                      className: classes?.root
                  })}
            data-theme={heroBannerTheme}
        >
            <div
                className={cn(s['wrapper'], {
                    'show-edges': showEdgesOfAdjacentSlide
                })}
                style={{ minHeight: slideHeight }}
                ref={backgroundElement}
            >
                {backgroundType === 'image' && (
                    <picture>
                        <source
                            media="(max-width: 1023px)"
                            srcSet={getFastlyUrl({
                                src: mobile_image!,
                                quality: imageQuality,
                                width: 800
                            })}
                        />
                        <source
                            media="(min-width: 1024px) and (max-width: 1600px)"
                            srcSet={getFastlyUrl({
                                src: desktop_image!,
                                quality: imageQuality,
                                width: 1500
                            })}
                        />
                        <source
                            media="(min-width: 1601px)"
                            srcSet={getFastlyUrl({
                                src: desktop_image!,
                                quality: imageQuality,
                                width: 2600
                            })}
                        />
                        <img
                            src={getFastlyUrl({
                                src: desktop_image!,
                                quality: imageQuality,
                                width: 1800
                            })}
                            // @ts-ignore
                            fetchPriority="high"
                            className={defaultClasses.image}
                            /* eslint-disable-next-line i18next/no-literal-string */
                            alt="OOKA  - Hero Banner"
                            style={wrapperStyles as CSSProperties}
                        />
                    </picture>
                )}
                <div
                    className={
                        appearanceOverlayClasses[
                            appearance in appearanceOverlayClasses
                                ? appearance
                                : 'collage-left'
                        ]
                    }
                >
                    {kicker && (
                        <p className={defaultClasses['hero-banner-kicker']}>
                            {kicker.substring(0, 25)}
                        </p>
                    )}
                    <HeadingTag
                        className={defaultClasses['hero-banner-heading']}
                    >
                        {heading?.substring(0, 40)}
                    </HeadingTag>
                    {subHeading && (
                        <p className={defaultClasses['hero-banner-subheading']}>
                            {subHeading?.substring(0, 100)}
                        </p>
                    )}
                    {buttonLink && BannerButton}
                </div>
                {backgroundType === 'video' && (
                    <div
                        ref={viewportElement}
                        className={classes.viewportElement}
                    />
                )}
            </div>
        </SlideWrapper>
    )
}

export default HeroBannerSlide
